import type {
  ReportDashboardConfig,
  ReportDashboardWidgetInstance,
  ReportDateRangeFilter,
  ReportFunnelStatusFunnelWidgetInstance,
  ReportFunnelStatusTimeseriesWidgetInstance,
  ReportPropertyInterestRankingWidgetInstance,
  ReportPropertyTypeDistributionWidgetInstance,
  ReportUserAssignmentRankingWidgetInstance,
  ReportWidgetValidation,
} from 'src/api/generated';
import type { TranslateParams } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { ReportDateRangePreset, ReportWidgetKind } from 'src/api/generated';

type ReportDashboardTranslator = (
  key: TranslationKey,
  params?: TranslateParams
) => string;

/**
 * Stable widget-kind constants sourced from the generated API models.
 */
export const REPORT_WIDGET_KIND = ReportWidgetKind;

/**
 * Normalizes generated widget discriminator enums into the shared catalog enum.
 */
export const toReportWidgetKind = (
  kind: ReportDashboardWidgetInstance['kind'] | ReportWidgetKind
): ReportWidgetKind => kind as unknown as ReportWidgetKind;

/**
 * Type guard for the funnel status funnel widget.
 */
export const isFunnelStatusFunnelWidget = (
  widget: ReportDashboardWidgetInstance
): widget is ReportFunnelStatusFunnelWidgetInstance =>
  'statusIds' in widget.filters;

/**
 * Type guard for the funnel status timeseries widget.
 */
export const isFunnelStatusTimeseriesWidget = (
  widget: ReportDashboardWidgetInstance
): widget is ReportFunnelStatusTimeseriesWidgetInstance =>
  'statusId' in widget.filters;

/**
 * Type guard for the property interest ranking widget.
 */
export const isPropertyInterestRankingWidget = (
  widget: ReportDashboardWidgetInstance
): widget is ReportPropertyInterestRankingWidgetInstance =>
  'limit' in widget.filters;

/**
 * Type guard for the assignment ranking widget.
 */
export const isUserAssignmentRankingWidget = (
  widget: ReportDashboardWidgetInstance
): widget is ReportUserAssignmentRankingWidgetInstance =>
  'visibleStatusIds' in widget.filters;

/**
 * Type guard for the property type distribution widget.
 */
export const isPropertyTypeDistributionWidget = (
  widget: ReportDashboardWidgetInstance
): widget is ReportPropertyTypeDistributionWidgetInstance =>
  !isFunnelStatusFunnelWidget(widget) &&
  !isFunnelStatusTimeseriesWidget(widget) &&
  !isPropertyInterestRankingWidget(widget) &&
  !isUserAssignmentRankingWidget(widget);

/**
 * Creates a new random widget instance identifier.
 */
export const createReportWidgetId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `widget-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;
};

/**
 * Creates a fresh widget draft by cloning the backend-provided default widget template.
 */
export const createReportWidgetDraft = (params: {
  templateWidget: ReportDashboardWidgetInstance;
  position: number;
}): ReportDashboardWidgetInstance => {
  return {
    ...params.templateWidget,
    id: createReportWidgetId(),
    layout: {
      ...params.templateWidget.layout,
      position: params.position,
    },
  };
};

/**
 * Creates a widget duplicate with a fresh id and trailing position.
 */
export const duplicateReportWidget = (
  widget: ReportDashboardWidgetInstance,
  position: number,
  copySuffix: string
): ReportDashboardWidgetInstance => ({
  ...widget,
  id: createReportWidgetId(),
  title: widget.title ? `${widget.title} ${copySuffix}` : widget.title,
  layout: {
    ...widget.layout,
    position,
  },
});

/**
 * Returns a human-readable date range label for a widget.
 */
export const formatReportDateRangeLabel = (
  dateRange: ReportDateRangeFilter,
  t: ReportDashboardTranslator
): string => {
  switch (dateRange.preset) {
    case ReportDateRangePreset.TODAY:
      return t('reports.dashboard.datePreset.today');
    case ReportDateRangePreset.LAST_7_DAYS:
      return t('reports.dashboard.datePreset.last7Days');
    case ReportDateRangePreset.THIS_MONTH:
      return t('reports.dashboard.datePreset.thisMonth');
    case ReportDateRangePreset.LAST_30_DAYS:
      return t('reports.dashboard.datePreset.last30Days');
    case ReportDateRangePreset.CUSTOM_RANGE:
      return dateRange.from && dateRange.to
        ? `${dateRange.from} - ${dateRange.to}`
        : t('reports.dashboard.datePreset.customRange');
    default:
      return t('reports.dashboard.datePreset.customRange');
  }
};

/**
 * Normalizes widget positions after local edits.
 */
export const normalizeDashboardWidgetPositions = (
  widgets: ReportDashboardWidgetInstance[]
): ReportDashboardWidgetInstance[] =>
  [...widgets]
    .sort((left, right) => left.layout.position - right.layout.position)
    .map((widget, index) => ({
      ...widget,
      layout: {
        ...widget.layout,
        position: index,
      },
    }));

/**
 * Replaces or inserts a widget in a dashboard config.
 */
export const upsertDashboardWidget = (
  config: ReportDashboardConfig,
  widget: ReportDashboardWidgetInstance
): ReportDashboardConfig => {
  const nextWidgets = config.widgets.some((current) => current.id === widget.id)
    ? config.widgets.map((current) =>
        current.id === widget.id ? widget : current
      )
    : [...config.widgets, widget];

  return {
    ...config,
    widgets: normalizeDashboardWidgetPositions(nextWidgets),
  };
};

/**
 * Removes a widget from a dashboard config.
 */
export const removeDashboardWidget = (
  config: ReportDashboardConfig,
  widgetId: string
): ReportDashboardConfig => ({
  ...config,
  widgets: normalizeDashboardWidgetPositions(
    config.widgets.filter((widget) => widget.id !== widgetId)
  ),
});

/**
 * Reorders dashboard widgets after drag-and-drop.
 */
export const reorderDashboardWidgets = (
  config: ReportDashboardConfig,
  orderedIds: string[]
): ReportDashboardConfig => {
  const byId = new Map(config.widgets.map((widget) => [widget.id, widget]));
  const ordered = orderedIds
    .map((id) => byId.get(id))
    .filter((widget): widget is ReportDashboardWidgetInstance =>
      Boolean(widget)
    );
  const orderedIdSet = new Set(orderedIds);
  const trailingWidgets = config.widgets.filter(
    (widget) => !orderedIdSet.has(widget.id)
  );

  return {
    ...config,
    widgets: [...ordered, ...trailingWidgets].map((widget, index) => ({
      ...widget,
      layout: {
        ...widget.layout,
        position: index,
      },
    })),
  };
};

/**
 * Returns whether a widget can be queried safely.
 */
export const isReportWidgetQueryable = (
  validation?: ReportWidgetValidation
): boolean => validation?.isValid !== false;
